import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Database, Building2, Hourglass, Plus, Trash2, Save, MessageSquareText } from "lucide-react";
import BrandingCard from "@/components/BrandingCard";
import PdfPreviewCard from "@/components/PdfPreviewCard";
import EmailPreviewCard from "@/components/EmailPreviewCard";

const AGENCE_FIELDS = [
  ["nom", "Raison sociale"], ["adresse", "Adresse (rue, NPA localité)"], ["telephone", "Téléphone"],
  ["email", "E-mail"], ["web", "Site web"], ["iban", "IBAN"],
];

export default function Settings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agence, setAgence] = useState(null);
  const [vetuste, setVetuste] = useState(null);
  const [remarques, setRemarques] = useState(null);

  const load = async () => {
    const [s, a, v, r] = await Promise.all([api.get("/admin/reference-status"), api.get("/settings/agence"), api.get("/settings/vetuste"), api.get("/settings/remarques")]);
    setStatus(s.data); setAgence(a.data); setVetuste(v.data); setRemarques(r.data);
  };
  useEffect(() => { load(); }, []);

  const init = async (reset = false) => {
    if (reset && !window.confirm("Remettre le catalogue et les typologies aux valeurs par défaut ? Vos personnalisations seront perdues (les constats existants ne sont pas touchés).")) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/init-reference-data${reset ? "?confirm=REINITIALISER" : ""}`);
      toast.success(`${data.message} : ${data.pieces} types, ${data.elements} éléments, ${data.typologies} typologies ajoutés`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const saveAgence = async () => {
    try { setAgence((await api.put("/settings/agence", agence)).data); toast.success("Coordonnées enregistrées"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveRemarques = async () => {
    try { setRemarques((await api.put("/settings/remarques", { items: remarques })).data); toast.success("Formulations enregistrées"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveVetuste = async () => {
    try { setVetuste((await api.put("/settings/vetuste", { items: vetuste })).data); toast.success("Tabelle de vétusté enregistrée"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-dd-blue mb-1">Réglages</h1>
        <p className="text-sm text-dd-warm">Coordonnées de l'agence, tabelle de vétusté et données de référence.</p>
      </div>

      <BrandingCard />
      <PdfPreviewCard />
      <EmailPreviewCard />

      <Card icon={Building2} title="Coordonnées de l'agence (pied de page PDF, page de connexion)" testid="agence-card">
        {agence && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              {AGENCE_FIELDS.map(([k, label]) => (
                <div key={k} className={k === "adresse" || k === "iban" ? "sm:col-span-2" : ""}>
                  <Label className="text-dd-night text-sm">{label}</Label>
                  <Input value={agence[k] || ""} onChange={(e) => setAgence({ ...agence, [k]: e.target.value })} className="h-11 mt-1" data-testid={`agence-${k}`} />
                </div>
              ))}
            </div>
            <Button onClick={saveAgence} className="mt-4 h-11 bg-dd-blue hover:bg-dd-night text-white" data-testid="agence-save"><Save size={16} className="mr-1" /> Enregistrer</Button>
          </>
        )}
      </Card>

      <Card icon={Hourglass} title="Tabelle de vétusté (durées de vie)" testid="vetuste-card">
        <p className="text-sm text-dd-warm mt-1">Tabelle paritaire ASLOCA/FRI simplifiée. Part locataire = coût × (1 − âge / durée de vie).</p>
        {vetuste && (
          <>
            <div className="mt-3 space-y-2">
              {vetuste.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_110px_44px] gap-2 items-center">
                  <Input value={it.libelle} onChange={(e) => setVetuste(vetuste.map((x, j) => j === i ? { ...x, libelle: e.target.value } : x))} className="h-11" data-testid={`vet-libelle-${i}`} />
                  <div className="flex items-center gap-1"><Input type="number" value={it.duree} onChange={(e) => setVetuste(vetuste.map((x, j) => j === i ? { ...x, duree: parseInt(e.target.value, 10) || 0 } : x))} className="h-11" data-testid={`vet-duree-${i}`} /><span className="text-xs text-dd-warm">ans</span></div>
                  <button onClick={() => setVetuste(vetuste.filter((_, j) => j !== i))} className="h-11 flex items-center justify-center text-destructive" data-testid={`vet-delete-${i}`}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setVetuste([...vetuste, { libelle: "", duree: 10 }])} className="h-11 border-dd-blue text-dd-blue" data-testid="vet-add"><Plus size={16} className="mr-1" /> Ajouter un poste</Button>
              <Button onClick={saveVetuste} className="h-11 bg-dd-blue hover:bg-dd-night text-white" data-testid="vet-save"><Save size={16} className="mr-1" /> Enregistrer</Button>
            </div>
          </>
        )}
      </Card>

      <Card icon={MessageSquareText} title="Formulations types de défauts" testid="remarques-card">
        <p className="text-sm text-dd-warm mt-1">Insérées d'un tap dans le commentaire d'une observation. Les mots-clés (séparés par des espaces) font remonter la formulation en premier pour les éléments correspondants.</p>
        {remarques && (
          <>
            <div className="mt-3 space-y-2">
              {remarques.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_200px_44px] gap-2 items-center">
                  <Input value={it.texte} placeholder="Formulation" onChange={(e) => setRemarques(remarques.map((x, j) => j === i ? { ...x, texte: e.target.value } : x))} className="h-11" data-testid={`rem-texte-${i}`} />
                  <Input value={it.mots_cles || ""} placeholder="mots-clés (mur sol porte…)" onChange={(e) => setRemarques(remarques.map((x, j) => j === i ? { ...x, mots_cles: e.target.value } : x))} className="h-11" data-testid={`rem-mots-${i}`} />
                  <button onClick={() => setRemarques(remarques.filter((_, j) => j !== i))} className="h-11 flex items-center justify-center text-destructive" data-testid={`rem-delete-${i}`}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setRemarques([...remarques, { texte: "", mots_cles: "" }])} className="h-11 border-dd-blue text-dd-blue" data-testid="rem-add"><Plus size={16} className="mr-1" /> Ajouter une formulation</Button>
              <Button onClick={saveRemarques} className="h-11 bg-dd-blue hover:bg-dd-night text-white" data-testid="rem-save"><Save size={16} className="mr-1" /> Enregistrer</Button>
            </div>
          </>
        )}
      </Card>

      <Card icon={Database} title="Initialiser les données de référence">
        <p className="text-sm text-dd-warm mt-1">
          Insère (ou réinsère) les typologies et le catalogue d'éléments fournis. À exécuter notamment en production,
          car la base de preview et la base de production ne communiquent jamais.
        </p>
        {status && (
          <div className="flex flex-wrap gap-4 mt-4 text-sm">
            <Stat label="Types de pièces" value={status.pieces} />
            <Stat label="Éléments" value={status.elements} />
            <Stat label="Typologies" value={status.typologies} />
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-5">
          <Button onClick={() => init(false)} disabled={loading} data-testid="init-reference-btn" className="h-11 bg-dd-blue hover:bg-dd-night text-white">
            {loading ? "Initialisation…" : "Compléter les données manquantes"}
          </Button>
          <Button onClick={() => init(true)} disabled={loading} variant="outline" data-testid="reset-reference-btn" className="h-11 border-destructive text-destructive hover:bg-destructive/10">
            Réinitialiser aux valeurs par défaut
          </Button>
        </div>
        <p className="text-xs text-dd-warm mt-3">« Compléter » ajoute uniquement les types, éléments et typologies absents sans toucher à vos personnalisations. « Réinitialiser » remplace tout le catalogue vivant (confirmation demandée). Les constats déjà créés ne sont jamais affectés.</p>
      </Card>
    </div>
  );
}

function Card({ icon: Icon, title, children, testid }) {
  return (
    <div className="bg-white rounded-lg border border-dd-light p-6" data-testid={testid}>
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-lg bg-dd-blue/10 flex items-center justify-center shrink-0"><Icon className="text-dd-blue" size={22} /></div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-dd-blue">{title}</h3>
          {children}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="px-3 py-2 rounded-md bg-dd-light/40">
      <div className="text-xs text-dd-warm">{label}</div>
      <div className="text-lg font-semibold text-dd-blue">{value}</div>
    </div>
  );
}
